import React, { useCallback, useEffect, useRef, useState } from 'react';
import Observables from '@happy-gastro/global-observables';
import { navigationRef } from '../../helpers/navigationRef';
import { getUserInfo } from '../../helpers/storage';
import ModuleWhatsNewDialog from './ModuleWhatsNewDialog';
import { getCoordinatorState,
  getUnseenModuleEntries,
  markSeen,
  subscribeCoordinator,
  subscribeRequests } from './moduleWhatsNewStore';

/** Delay after navigation before the modal appears, so the screen paints first. */
const SHOW_DELAY_MS = 900;
/** If the global What's New never reports in, stop waiting after this. */
const GLOBAL_WAIT_CAP_MS = 8000;

/**
 * Collect the role strings off a stored user object (roles / effective / default).
 * @param {object} user Stored user.
 * @returns {string[]}
 */
const collectRoles = (user) => {
  const out = [];
  const add = (v) => {
    if (Array.isArray(v)) {
      v.forEach(add);
    } else if (v) {
      out.push(String(v));
    }
  };

  add(user?.roles);
  add(user?.effectiveRoles);
  add(user?.defaultRoles);

  return out;
};

/**
 * ModuleWhatsNewMount — the single, headless watcher behind the modular What's New.
 *
 * Mounted ONCE in `AppNavigator` (inside the NavigationContainer, next to MyDayBriefing) and only
 * active for signed-in sessions. It follows the focused route through the navigation container's
 * `state` event; on the user's first visit to a screen that a registry entry lists in `routes`, it
 * opens ONE premium modal with that module's unseen entries. Dismissing marks them seen (per user,
 * AsyncStorage). It also serves `useModuleWhatsNew(moduleKey)` requests for views with no screen.
 *
 * Never shows while the global What's New is open, nor before its upgrade check has settled.
 *
 * @param {object} props
 * @param {boolean} props.enabled Whether a user session exists.
 * @returns {?JSX.Element}
 */
const ModuleWhatsNewMount = ({ enabled }) => {
  const [user, setUser] = useState({ id: '', createdAt: null, internal: false });
  const [route, setRoute] = useState(null);
  const [active, setActive] = useState(null);
  const [coordinator, setCoordinator] = useState(getCoordinatorState());
  const [waitedForGlobal, setWaitedForGlobal] = useState(false);
  const timerRef = useRef(null);
  const activeRef = useRef(null);
  const routeRef = useRef(null);
  const userRef = useRef(user);
  const pendingRef = useRef(null);
  const readyRef = useRef(false);

  activeRef.current = active;
  routeRef.current = route;
  userRef.current = user;

  const ready = coordinator.globalSettled || waitedForGlobal;
  const blocked = coordinator.globalOpen || Boolean(active);

  readyRef.current = ready;

  useEffect(() => subscribeCoordinator(setCoordinator), []);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => setWaitedForGlobal(true), GLOBAL_WAIT_CAP_MS);

    return () => globalThis.clearTimeout(timer);
  }, []);

  // ---- Who is signed in ----------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setUser({ id: '', createdAt: null, internal: false });
      setActive(null);

      return undefined;
    }
    getUserInfo().then((u) => {
      if (!cancelled) {
        setUser({
          id: String(u?._id || u?.id || ''),
          createdAt: u?.createdAt || null,
          internal: collectRoles(u).includes('system:admin'),
        });
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [enabled]);

  // ---- Follow the focused route through the navigation container -----------------------------
  useEffect(() => {
    if (!enabled) {
      setRoute(null);

      return undefined;
    }
    const sync = () => {
      try {
        if (navigationRef.isReady()) {
          const current = navigationRef.getCurrentRoute();

          setRoute(current ? { name: current.name, params: current.params || null } : null);
        }
      } catch (_e) {
        // Container not ready yet; the next state event retries.
      }
    };

    sync();
    // The container may become ready after this mount; catch the initial route once it has.
    const retry = globalThis.setTimeout(sync, 1500);
    const unsubscribe = navigationRef.addListener('state', sync);

    return () => {
      globalThis.clearTimeout(retry);
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [enabled]);

  // ---- Route-driven: first visit to a matching screen ----------------------------------------
  useEffect(() => {
    globalThis.clearTimeout(timerRef.current);
    if (!enabled || !ready || blocked || !user.id || !route) {
      return undefined;
    }
    timerRef.current = globalThis.setTimeout(async () => {
      const entries = await getUnseenModuleEntries({
        userId: user.id,
        route,
        userCreatedAt: user.createdAt,
        includeInternal: user.internal,
      });

      // Still on that screen, and nothing else took the stage while we were reading storage.
      if (entries.length > 0 && routeRef.current === route && !activeRef.current && !getCoordinatorState().globalOpen) {
        setActive({ moduleKey: entries[0].module, entries });
      }
    }, SHOW_DELAY_MS);

    return () => globalThis.clearTimeout(timerRef.current);
  }, [enabled, ready, blocked, user, route]);

  // ---- Hook-driven: views without a screen of their own -------------------------------------
  // A request that arrives while another modal is up (or before the global check settled) is queued
  // and served once the way is clear, so a view's auto-open is never silently lost.
  const showModule = useCallback(async (moduleKey, force) => {
    const current = userRef.current;
    const entries = await getUnseenModuleEntries({
      userId: current.id,
      module: moduleKey,
      force,
      userCreatedAt: current.createdAt,
      includeInternal: current.internal,
    });

    if (entries.length > 0 && !activeRef.current && !getCoordinatorState().globalOpen) {
      setActive({ moduleKey, entries });
    }
  }, []);

  useEffect(() => subscribeRequests(({ module: moduleKey, force }) => {
    pendingRef.current = { moduleKey, force };
    if (!readyRef.current || activeRef.current || getCoordinatorState().globalOpen || !userRef.current.id) {
      return;
    }
    const pending = pendingRef.current;

    pendingRef.current = null;
    showModule(pending.moduleKey, pending.force);
  }), [showModule]);

  useEffect(() => {
    if (!ready || blocked || !user.id || !pendingRef.current) {
      return;
    }
    const pending = pendingRef.current;

    pendingRef.current = null;
    showModule(pending.moduleKey, pending.force);
  }, [ready, blocked, user.id, showModule]);

  const handleClose = useCallback(async () => {
    const current = activeRef.current;

    if (current) {
      // Persist BEFORE unmounting: closing re-runs the route watcher, which must already see these as seen.
      await markSeen(userRef.current.id, current.entries.map((entry) => entry.id));
    }
    setActive(null);
  }, []);

  const handleSeeAll = useCallback(async () => {
    await handleClose();
    // Let this modal unmount first so only one What's New surface is ever on screen.
    globalThis.setTimeout(() => Observables.call('openWhatsNew'), 350);
  }, [handleClose]);

  if (!active) {
    return null;
  }

  return (
    <ModuleWhatsNewDialog
      visible
      moduleKey={active.moduleKey}
      entries={active.entries}
      onClose={handleClose}
      onSeeAll={handleSeeAll}
    />
  );
};

export default ModuleWhatsNewMount;
